import React, { useState, useEffect } from 'react';
import { RefreshCcw, Maximize2, Minimize2, X } from 'lucide-react';
import '../components/Dashboard.css';

const FlashcardsModal = ({ isOpen, onClose, selectedDocument, user }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [flashcardLoading, setFlashcardLoading] = useState(false);
    const [flashcardResponse, setFlashcardResponse] = useState(null);
    const [currentFlashcardIndex, setCurrentFlashcardIndex] = useState(0);
    const [showFlashcardAnswer, setShowFlashcardAnswer] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            console.log('FlashcardsModal opened with selectedDocument:', selectedDocument);
            setFlashcardResponse(null);
            setCurrentFlashcardIndex(0);
            setShowFlashcardAnswer(false);
            setFlashcardLoading(false);
        }
    }, [isOpen, selectedDocument]);

    const generateNewFlashcards = async () => {
        if (!selectedDocument || flashcardLoading) return;

        console.log('Generating flashcards for document:', selectedDocument);
        setFlashcardLoading(true);
        setCurrentFlashcardIndex(0);
        setShowFlashcardAnswer(false);

        try {
            const response = await fetch('https://celebrated-charisma-production-5701.up.railway.app/generate_flashcards', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user?.uid,
                    document_id: selectedDocument?.id || selectedDocument?.document_id || selectedDocument?.uuid
                })
            });

            if (!response.ok) {
                throw new Error('Failed to generate flashcards');
            }

            const data = await response.json();
            setFlashcardResponse(data);
        } catch (error) {
            console.error('Flashcard generation error:', error);
            alert('Failed to generate flashcards. Please try again.');
        } finally {
            setFlashcardLoading(false);
        }
    };

    const shuffleFlashcards = () => {
        if (flashcardResponse && flashcardResponse.flashcards) {
            const shuffled = [...flashcardResponse.flashcards].sort(() => Math.random() - 0.5);
            setFlashcardResponse(prev => ({ ...prev, flashcards: shuffled }));
            setCurrentFlashcardIndex(0);
            setShowFlashcardAnswer(false);
        }
    };

    const nextFlashcard = () => {
        if (flashcardResponse && currentFlashcardIndex < flashcardResponse.flashcards.length - 1) {
            setCurrentFlashcardIndex(prev => prev + 1);
            setShowFlashcardAnswer(false);
        }
    };

    const prevFlashcard = () => {
        if (currentFlashcardIndex > 0) {
            setCurrentFlashcardIndex(prev => prev - 1);
            setShowFlashcardAnswer(false);
        }
    };

    const reviewFlashcard = (flashcardId, difficulty) => {
        // Handle flashcard review - could send to backend for spaced repetition
        console.log('Reviewing flashcard:', flashcardId, 'difficulty:', difficulty);
        setShowFlashcardAnswer(false);
        nextFlashcard();
    };

    if (!isOpen) return null;

    return (
        <div className="flashcard-modal-overlay" onClick={onClose}>
            <div className={`flashcard-modal-container ${isExpanded ? 'expanded' : ''}`} onClick={(e) => e.stopPropagation()}>
                <div className="flashcard-modal-header">
                    <h1>Study Flashcards</h1>
                    <div className="flashcard-modal-controls">
                        <button 
                            className="flashcard-shuffle-btn" 
                            onClick={flashcardResponse && flashcardResponse.flashcards && flashcardResponse.flashcards.length > 0 ? shuffleFlashcards : generateNewFlashcards}
                            title={flashcardResponse && flashcardResponse.flashcards && flashcardResponse.flashcards.length > 0 ? 'Shuffle Cards' : 'Generate Flashcards'}
                            disabled={flashcardLoading || !selectedDocument}
                        >
                            <RefreshCcw size={16} />
                        </button>
                        <button 
                            className="flashcard-expand-btn" 
                            onClick={() => setIsExpanded(!isExpanded)}
                            title={isExpanded ? 'Minimize' : 'Expand'}
                        >
                            {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                        </button>
                        <button className="flashcard-close-btn" onClick={onClose}><X className='flashcard-close-btn-icon' /></button>
                    </div>
                </div>

                {flashcardResponse && flashcardResponse.flashcards && flashcardResponse.flashcards.length > 0 && (
                    <div className='flashcard-source-info'>
                        <p>current flashcards from: {selectedDocument?.document_name || selectedDocument?.name}</p>
                    </div>
                )}
                
                <div className="flashcard-modal-content">
                    {flashcardLoading ? (
                        <div className="flashcard-loading-state">
                            <div className="flashcard-loading-spinner"></div>
                            <p>Generating flashcards...</p>
                        </div>
                    ) : flashcardResponse && flashcardResponse.flashcards && flashcardResponse.flashcards.length > 0 ? (
                        <div className="flashcard-study-area">
                            <div className="flashcard-navigation">
                                <button 
                                    className="flashcard-nav-btn prev-btn" 
                                    onClick={prevFlashcard} 
                                    disabled={currentFlashcardIndex === 0}
                                >
                                    ←
                                </button>
                                <span className="flashcard-counter">
                                    {currentFlashcardIndex + 1} / {flashcardResponse.flashcards.length}
                                </span>
                                <button 
                                    className="flashcard-nav-btn next-btn" 
                                    onClick={nextFlashcard} 
                                    disabled={currentFlashcardIndex === flashcardResponse.flashcards.length - 1}
                                >
                                    →
                                </button>
                            </div>
                            
                            <div className="flashcard-display-area">
                                <div 
                                    className={`flashcard-card ${showFlashcardAnswer ? 'flipped' : ''}`}
                                    onClick={() => setShowFlashcardAnswer(!showFlashcardAnswer)}
                                >
                                    <div className="flashcard-card-inner">
                                        <div className="flashcard-card-front">
                                            <div className="flashcard-text">
                                                <p>{flashcardResponse.flashcards[currentFlashcardIndex]?.front}</p>
                                            </div>
                                        </div>
                                        <div className="flashcard-card-back">
                                            <div className="flashcard-text">
                                                <p>{flashcardResponse.flashcards[currentFlashcardIndex]?.back}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flashcard-actions-area">
                                {!showFlashcardAnswer ? (
                                    <button onClick={() => setShowFlashcardAnswer(true)} className="show-answer-button">
                                        SHOW ANSWER
                                    </button>
                                ) : (
                                    <div className="difficulty-feedback">
                                        <p>How difficult was this?</p>
                                        <div className="difficulty-buttons">
                                            <button onClick={() => reviewFlashcard(flashcardResponse.flashcards[currentFlashcardIndex].id, 0.2)} className="difficulty-btn easy">
                                                Easy
                                            </button>
                                            <button onClick={() => reviewFlashcard(flashcardResponse.flashcards[currentFlashcardIndex].id, 0.5)} className="difficulty-btn medium">
                                                Medium
                                            </button>
                                            <button onClick={() => reviewFlashcard(flashcardResponse.flashcards[currentFlashcardIndex].id, 0.8)} className="difficulty-btn hard">
                                                Hard
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="flashcard-empty-state">
                            <div className="flashcard-generate-prompt">
                                {!selectedDocument ? (
                                    <>
                                        <p className="flashcard-error-message">
                                            ⚠️ No document selected. Please select a document first to generate flashcards.
                                        </p>
                                        <button 
                                            className="create-flashcards-btn disabled"
                                            disabled={true}
                                            onClick={() => alert('To create flashcards:\n\n1. Go to "My Files" page\n2. Upload a PDF document\n3. Click on the document to select it\n4. Return here and click "Create Flashcards"')}
                                        >
                                            + Create Flashcards
                                        </button>
                                        <p className="flashcard-help-text">
                                            Go to "My Files" to upload and select a document, then return here to create flashcards.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <div className="flashcard-selected-document">
                                            <p className="flashcard-success-message">
                                                ✅ Document selected: <strong>{selectedDocument.document_name || selectedDocument.name || 'Untitled Document'}</strong>
                                            </p>
                                        </div>
                                        <p>Click the button below to generate flashcards from your selected document.</p>
                                        <button 
                                            className="create-flashcards-btn"
                                            onClick={generateNewFlashcards}
                                            disabled={flashcardLoading}
                                        >
                                            {flashcardLoading ? 'Generating...' : '+ Create Flashcards'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FlashcardsModal;
